import { CircleAlert, Send } from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod'

import { AuthErrorBanner } from '@/customer/components/auth/auth-error-banner'
import { Alert, AlertDescription } from '@/customer/components/ui/alert'
import { Button } from '@/customer/components/ui/button'
import { Input } from '@/customer/components/ui/input'

const emailSchema = z.email()

/**
 * Entry step of the "change my email" flow. Owns the local draft +
 * validation; the caller receives the submitted email through
 * `onSubmit`.
 *
 * When `currentValue` is supplied, entering the same email is
 * blocked with a nudge to the verify page - matching better-auth's
 * email OTP plugin, which rejects `changeEmail` when the new value
 * equals the current one.
 */
export function EmailEntryPanel({
  currentValue,
  errorCode,
  isSubmitting,
  onSubmit,
}: {
  currentValue?: string | null
  errorCode: string | null
  isSubmitting: boolean
  onSubmit: (email: string) => void | Promise<void>
}) {
  const [draft, setDraft] = useState('')
  const draftIsValid = emailSchema.safeParse(draft).success
  const isSameAsCurrent =
    draftIsValid &&
    currentValue !== undefined &&
    draft.toLowerCase() === currentValue?.toLowerCase()

  return (
    <div className="flex flex-col gap-6">
      <p className="text-body-md text-foreground">
        Enter the new email address you&apos;d like to use. We&apos;ll send a 6-digit code to
        confirm it&apos;s yours.
      </p>

      <Input
        type="email"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        aria-invalid={errorCode !== null}
        placeholder="you@example.com"
        autoComplete="email"
        autoFocus
      />

      {isSameAsCurrent ? (
        <Alert variant="warning">
          <CircleAlert />
          <AlertDescription>That's your current email - try a different one.</AlertDescription>
        </Alert>
      ) : (
        <AuthErrorBanner code={errorCode} />
      )}

      <Button
        type="button"
        onClick={() => {
          if (draftIsValid && !isSameAsCurrent) void onSubmit(draft)
        }}
        disabled={!draftIsValid || isSameAsCurrent || isSubmitting}
        className="h-14 w-full"
      >
        <Send className="size-5" />
        Send code
      </Button>
    </div>
  )
}
